// Converts between a clinical document's stored text and the editor's
// document model. Phase 1 of docs/HMS_V4_AI_CONTEXT_BUILDING_ASSESSMENT.md
// (§5.2 C11).
//
// Documents in this app are LINE-oriented: "Chief complaint: …" and
// "Pre-consult triage: …" on consecutive lines are separate lines, and
// backend/utils/docxGenerator.js splits on every newline when it builds the
// Word document. A standard markdown parser would merge those consecutive
// lines into one paragraph and silently join them in the saved text, so this
// converter is deliberately simple:
//
//   one line          ⇄ one paragraph
//   **text**          ⇄ bold      (a whole-line **Heading** stays a heading
//                                  when docxGenerator reads it back)
//   *text*            ⇄ italic
//   ***text***        ⇄ bold + italic
//   {{kind|Label…}}   ⇄ a smartField chip carrying the raw placeholder
//   anything else     ⇄ literal text — "Rx." lines, "| table |" rows,
//                       "---" markers and "_(…)_" all pass through unchanged
//
// The contract: docToMarkdown(markdownToDoc(text)) === text for any text,
// so opening a draft and saving it without edits never changes it.
//
// No imports, so it can be compiled and checked on its own.

export type MarkJSON = { type: 'bold' } | { type: 'italic' };

export type InlineJSON =
  | { type: 'text'; text: string; marks?: MarkJSON[] }
  | { type: 'smartField'; attrs: { raw: string } }
  | { type: 'hardBreak' };

export interface ParagraphJSON {
  type: 'paragraph';
  content?: InlineJSON[];
}

export interface DocJSON {
  type: 'doc';
  content: ParagraphJSON[];
}

const PLACEHOLDER_SPLIT = /(\{\{[^{}\n]{1,200}\}\})/;

// Longest first, so ***x*** isn't read as *(**x**)*. Italic needs a non-space
// right after its opening star, so arithmetic like "2 * 3 * 4" stays literal.
const EMPHASIS_SPLIT = /(\*\*\*[^*\n]+?\*\*\*|\*\*[^*\n]+?\*\*|\*[^*\s][^*\n]*?\*)/;

function emphasisNodes(segment: string): InlineJSON[] {
  const nodes: InlineJSON[] = [];
  for (const part of segment.split(EMPHASIS_SPLIT)) {
    if (!part) continue;
    if (part.startsWith('***') && part.endsWith('***') && part.length > 6) {
      nodes.push({ type: 'text', text: part.slice(3, -3), marks: [{ type: 'bold' }, { type: 'italic' }] });
    } else if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      nodes.push({ type: 'text', text: part.slice(2, -2), marks: [{ type: 'bold' }] });
    } else if (part.startsWith('*') && part.endsWith('*') && part.length > 2 && !/\s/.test(part[1]!)) {
      nodes.push({ type: 'text', text: part.slice(1, -1), marks: [{ type: 'italic' }] });
    } else {
      nodes.push({ type: 'text', text: part });
    }
  }
  return nodes;
}

function lineToParagraph(line: string): ParagraphJSON {
  const content: InlineJSON[] = [];
  for (const part of line.split(PLACEHOLDER_SPLIT)) {
    if (!part) continue;
    if (PLACEHOLDER_SPLIT.test(part) && part.startsWith('{{') && part.endsWith('}}')) {
      content.push({ type: 'smartField', attrs: { raw: part } });
    } else {
      content.push(...emphasisNodes(part));
    }
  }
  return content.length ? { type: 'paragraph', content } : { type: 'paragraph' };
}

export function markdownToDoc(markdown: string): DocJSON {
  const lines = String(markdown ?? '').replace(/\r\n?/g, '\n').split('\n');
  return { type: 'doc', content: lines.map(lineToParagraph) };
}

function hasMark(node: { marks?: MarkJSON[] }, type: MarkJSON['type']): boolean {
  return !!node.marks?.some((m) => m.type === type);
}

function wrap(text: string, bold: boolean, italic: boolean): string {
  if (!text) return '';
  if (bold && italic) return `***${text}***`;
  if (bold) return `**${text}**`;
  if (italic) return `*${text}*`;
  return text;
}

function paragraphToLine(paragraph: ParagraphJSON): string {
  let line = '';
  // Adjacent text nodes with the same marks are joined before wrapping, so a
  // bold run the editor split into pieces serialises as one **run**.
  let pendingText = '';
  let pendingBold = false;
  let pendingItalic = false;

  const flush = () => {
    line += wrap(pendingText, pendingBold, pendingItalic);
    pendingText = '';
  };

  for (const node of paragraph.content ?? []) {
    if (node.type === 'text') {
      const bold = hasMark(node, 'bold');
      const italic = hasMark(node, 'italic');
      if (pendingText && (bold !== pendingBold || italic !== pendingItalic)) flush();
      pendingBold = bold;
      pendingItalic = italic;
      pendingText += node.text;
    } else {
      flush();
      line += node.type === 'smartField' ? node.attrs.raw : '\n';
    }
  }
  flush();
  return line;
}

export function docToMarkdown(doc: DocJSON): string {
  return (doc.content ?? []).map(paragraphToLine).join('\n');
}
